import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '@/api/commercial/accounts';
import { configurationProfileApi } from '@/api/production/configuration-profiles';
import { technologyApi } from '@/api/production/technologies';
import type { ConfigurationProfilesFiltersFormData } from '@/schemas/production/configuration-profiles-filters.schema';

interface UseConfigurationProfilesParams {
  page?: number;
  limit?: number;
  filters?: ConfigurationProfilesFiltersFormData;
}

function toApiStatus(status?: string) {
  if (!status || status === 'all') return undefined;
  if (status === 'ATIVO' || status === 'INATIVO' || status === 'AGUARDANDO_VALIDACAO') {
    return status;
  }
  if (status === 'pending') return 'AGUARDANDO_VALIDACAO' as const;
  return 'ATIVO' as const;
}

function toIsoDate(date?: Date) {
  return date ? date.toISOString() : undefined;
}

function toIsoEndOfDay(date?: Date) {
  if (!date) return undefined;
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toISOString();
}

function emptyToUndefined(value?: string) {
  return !value || value === 'all' ? undefined : value;
}

export function useConfigurationProfiles({
  page = 1,
  limit = 20,
  filters,
}: UseConfigurationProfilesParams) {
  const { data, isLoading } = useQuery({
    queryKey: ['configuration-profiles', page, limit, filters],
    queryFn: () =>
      configurationProfileApi.findMany({
        page,
        limit,
        search: filters?.name || undefined,
        technology_id: emptyToUndefined(filters?.technologyId),
        client_id: emptyToUndefined(filters?.clientId),
        status: toApiStatus(filters?.status),
        created_at_from: toIsoDate(filters?.createdAtRange?.from),
        created_at_to: toIsoEndOfDay(filters?.createdAtRange?.to),
        updated_at_from: toIsoDate(filters?.editedAtRange?.from),
        updated_at_to: toIsoEndOfDay(filters?.editedAtRange?.to),
      }),
  });

  return { data, isLoading };
}

export function useInactivateConfigurationProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configurationProfileApi.inactivateProfile(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configuration-profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['configuration-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['config-profiles'] });
    },
  });
}

export function useValidateConfigurationProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configurationProfileApi.validateProfile(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configuration-profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['configuration-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['config-profiles'] });
    },
  });
}

export function useReplaceConfigurationProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => configurationProfileApi.replaceProfile(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configuration-profiles'] });
    },
  });
}

export function useConfigurationProfilesTechnologies() {
  const { data, isLoading } = useQuery({
    queryKey: ['configuration-profiles-technologies'],
    queryFn: async () => {
      const result = await technologyApi.findMany({ limit: 100 });
      return result.docs;
    },
    staleTime: 5 * 60 * 1000,
  });

  return { technologies: data ?? [], isLoading };
}

export function useConfigurationProfilesClients(search?: string) {
  const { data, isLoading } = useQuery({
    queryKey: ['configuration-profiles-clients', search],
    queryFn: async () => {
      const result = await accountApi.findMany({
        search,
        limit: 50,
      });
      return result.docs;
    },
    enabled: search !== undefined && search.length >= 2,
    staleTime: 60 * 1000,
  });

  return { clients: data ?? [], isLoading };
}
